import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Home, MessageCircle, Plus, User, UserPlus, UserRound, Users, Settings as SettingsIcon } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const tabs = [
  { to: "/discover", icon: Home, label: "Home", testId: "nav-home" },
  { to: "/chats", icon: MessageCircle, label: "Chats", testId: "nav-chats" },
  { plus: true, testId: "nav-create" },
  { to: "/profile", icon: User, label: "Profile", testId: "nav-profile", isAvatar: true },
  { to: "/settings", icon: SettingsIcon, label: "Settings", testId: "nav-settings" },
];

const createOptions = [
  { key: "character", label: "Character", icon: UserPlus, to: "/create", desc: "A new AI character to chat with" },
  { key: "persona", label: "Persona", icon: UserRound, to: "/create-persona", desc: "Who you are in the story" },
  { key: "group", label: "Group chat", icon: Users, to: null, desc: "Coming soon — multi-character scenes" },
];

export default function BottomNav() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const avatarUrl = user?.avatar_url;

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-40 flex justify-center pb-safe-4 pt-1 pointer-events-none"
      >
        <div className="glass-strong pointer-events-auto flex items-center gap-2 rounded-full px-3 py-2 shadow-2xl">
          {tabs.map((t) => {
            if (t.plus) {
              return (
                <button
                  key="plus"
                  data-testid={t.testId}
                  onClick={() => setOpen(true)}
                  className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-lg shadow-violet-500/40"
                  aria-label="Create"
                >
                  <Plus size={22} strokeWidth={2} />
                </button>
              );
            }
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testId}
                className={({ isActive }) =>
                  `relative flex h-11 w-11 flex-col items-center justify-center rounded-full transition-all ${
                    isActive ? "bg-white/10 text-white" : "text-neutral-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {t.isAvatar && avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="You"
                    data-testid="nav-profile-avatar"
                    className="h-7 w-7 rounded-full object-cover border border-white/20"
                  />
                ) : (
                  <Icon size={20} strokeWidth={2} />
                )}
                <span className="sr-only">{t.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="glass-strong border-white/10 text-white h-auto rounded-t-3xl" data-testid="create-sheet">
          <VisuallyHidden>
            <SheetTitle>Create</SheetTitle>
            <SheetDescription>Choose what you want to create — a new character, a persona, or a group chat.</SheetDescription>
          </VisuallyHidden>
          <div className="mx-auto max-w-md pb-6 pt-2">
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-white/20" />
            <h2 className="font-display text-3xl text-center mb-6">Create</h2>
            <ul className="space-y-2">
              {createOptions.map(({ key, label, icon: Icon, to, desc }) => (
                <li key={key}>
                  <button
                    data-testid={`create-sheet-${key}`}
                    onClick={() => {
                      setOpen(false);
                      if (!to) return toast("Coming soon.");
                      nav(to);
                    }}
                    disabled={!to}
                    className="flex w-full items-center gap-4 rounded-2xl border border-white/5 bg-white/[0.02] p-4 text-left hover:bg-white/[0.05] transition disabled:opacity-50"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-200">
                      <Icon size={22} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-base font-medium flex items-center gap-2">
                        {label}
                        {!to && (
                          <span className="rounded-full bg-amber-500/15 border border-amber-400/30 px-2 py-0.5 text-[9px] uppercase tracking-widest text-amber-200">
                            Coming soon
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-400 mt-0.5">{desc}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
